import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';
import { validate as validateUUID } from 'uuid';

import { DefinitionColumn } from '@connect-services/client/models/export-xlsx.model';
import { EditModeStateService, EditState, EntityEdit } from '@connect-services/client/services/edit-mode-state.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import {
    EditAction,
    EditConflicts,
    EditConflictsDeletedEntitiesInner,
    EditModeUpsertResult,
    Layer,
    LayersService,
    Project,
} from '@connect-services/src/sdk/api';

type EditRowData = {
    action: EditAction;
    after: Record<string, unknown>;
    before: Record<string, unknown>;
    businessIdentifier: unknown;
    layerName?: string;
    propertyKeys: string[];
    state: string;
    status: ExportStatus;
};

type ResultEditRowData = EditRowData & { message: string | null; success: boolean };

const enum ExportStatus {
    NOT_APPLICABLE = 'not-applicable',
    POTENTIAL_CONFLICT = 'potential-conflict',
    VALID = 'valid',
}

const DEFAULT_COLUMN_WIDTH = 25;

@Injectable({
    providedIn: 'root',
})
export class ExportEditEntityService {
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly layersService = inject(LayersService);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private translocoService = inject(TranslocoService);
    private readonly xlsxColumns = [
        { header: this.translocoService.translate('businessIdentifier'), key: 'businessIdentifier' },
        { header: this.translocoService.translate('layerName'), key: 'layerName' },
        { header: this.translocoService.translate('editType'), key: 'action' },
        {
            header: this.translocoService.translate('modifiedAttributes'),
            key: 'propertyKeys',
        },
        { header: this.translocoService.translate('afterModification'), key: 'after' },
        { header: this.translocoService.translate('beforeModification'), key: 'before' },
        { header: this.translocoService.translate('state'), key: 'state' },
    ];
    private readonly xlsxResultColumns = [
        ...this.xlsxColumns,
        { header: this.translocoService.translate('success'), key: 'success' },
        { header: this.translocoService.translate('message'), key: 'message' },
    ];

    public async exportConflictsInXlsx(
        project: Project,
        projectState: EditState,
        editConflicts?: EditConflicts,
    ): Promise<void> {
        const editSessionInfo = this.projectEditModeService.getEditSession();
        if (!editSessionInfo) {
            return;
        }

        const workbook = this.initXlsx('detail', this.xlsxColumns);
        const sheet = workbook.getWorksheet(this.translocoService.translate('detail'));

        if (!sheet) {
            return;
        }

        const allRows: EditRowData[] = await this.getEditStateRowData(projectState, project.id, editConflicts);

        allRows.sort(this.sortRows.bind(this)).forEach((editRowData) => {
            const row = sheet.addRow(editRowData);
            this.applyRowFill(row, editRowData.status);
        });

        await this.downloadXlsx(workbook, project.name, editSessionInfo.id);
    }

    public async exportResultInXlsx(
        project: Project,
        projectState: EditState,
        editModeResults: EditModeUpsertResult[],
    ): Promise<void> {
        const editSessionInfo = this.projectEditModeService.getEditSession();
        if (!editSessionInfo) {
            return;
        }

        const workbook = this.initXlsx('result', this.xlsxResultColumns);
        const sheet = workbook.getWorksheet(this.translocoService.translate('result'));

        if (!sheet) {
            return;
        }

        const editStateRowData = await this.getEditStateRowData(projectState, project.id);

        editStateRowData.forEach((editRowData: EditRowData) => {
            const currentEditModeResult = editModeResults.find(
                (result: EditModeUpsertResult) => result.businessIdentifier === editRowData.businessIdentifier,
            );
            let finalRow: EditRowData | ResultEditRowData = editRowData;

            if (currentEditModeResult) {
                finalRow = {
                    ...editRowData,
                    message: currentEditModeResult.message,
                    success: currentEditModeResult.success,
                };
            }

            const row = sheet.addRow(finalRow);
            if (currentEditModeResult?.success !== false) {
                return;
            }

            row.eachCell({ includeEmpty: true }, (cell) => {
                cell.fill = {
                    fgColor: { argb: 'FF0000' },
                    pattern: 'solid',
                    type: 'pattern',
                };
            });
        });

        await this.downloadXlsx(workbook, project.name, editSessionInfo.id);
    }

    private applyRowFill(row: Excel.Row, status: ExportStatus): void {
        const fillRow = this.getFillForStatus(status);
        if (!fillRow) {
            return;
        }

        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = fillRow;
        });
    }

    private async downloadXlsx(workbook: Excel.Workbook, projectName: string, editSessionId: string): Promise<void> {
        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const formatted = new Date(Date.now()).toISOString().slice(0, 10);

        saveAs(blob, `${formatted}_${projectName}_${editSessionId}.xlsx`);
    }

    private async getEditStateRowData(
        projectState: EditState,
        projectId: string,
        editConflicts?: EditConflicts,
    ): Promise<EditRowData[]> {
        const layerIds = Object.keys(projectState).flat();
        const filteredLayerIds = layerIds.filter((layerId: string) => validateUUID(layerId));
        const layers: Layer[] = [];

        for (const layerId of filteredLayerIds) {
            try {
                const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId }));
                layers.push(layer);
            } catch {}
        }

        return Object.keys(projectState).flatMap((layerId: string) =>
            Object.entries(projectState[layerId]).reduce(
                (acc: EditRowData[], [entityId, entityEdit]: [string, EntityEdit]) => {
                    if (entityEdit.saveOrder === -1) {
                        return acc;
                    }

                    const filteredLayer = layers.find((layer: Layer) => layer.id === layerId);

                    acc.push(
                        this.mapToRowData(projectId, entityId, entityEdit, layerId, {
                            editConflicts,
                            layerName: filteredLayer?.name,
                        }),
                    );

                    return acc;
                },
                [],
            ),
        );
    }

    private getExportStatus(layerId: string, entityId: string, editConflicts?: EditConflicts): ExportStatus {
        if (!editConflicts) {
            return ExportStatus.VALID;
        }

        const isNotApplicable =
            this.isLayerDepositDeleted(layerId, editConflicts) || this.isEntityDeleted(entityId, editConflicts);

        if (isNotApplicable) {
            return ExportStatus.NOT_APPLICABLE;
        }

        const isEntityInModifiedConflicts = editConflicts.modifiedEntities.some(
            (editConflict: EditConflictsDeletedEntitiesInner) => editConflict.entityId === entityId,
        );

        if (isEntityInModifiedConflicts) {
            return ExportStatus.POTENTIAL_CONFLICT;
        }

        return ExportStatus.VALID;
    }

    private getFillForStatus(status: ExportStatus): Excel.Fill | undefined {
        if (status === ExportStatus.NOT_APPLICABLE) {
            return {
                fgColor: { argb: 'FF0000' },
                pattern: 'solid',
                type: 'pattern',
            };
        }

        if (status === ExportStatus.POTENTIAL_CONFLICT) {
            return {
                fgColor: { argb: 'FFC000' },
                pattern: 'solid',
                type: 'pattern',
            };
        }

        return undefined;
    }

    private getStateLabel(status: ExportStatus): string {
        if (status === ExportStatus.NOT_APPLICABLE) {
            return this.translocoService.translate('notApplicable');
        }

        if (status === ExportStatus.POTENTIAL_CONFLICT) {
            return this.translocoService.translate('potentialConflict');
        }

        return this.translocoService.translate('applicable');
    }

    private getStatusRank(status: ExportStatus): number {
        if (status === ExportStatus.NOT_APPLICABLE) {
            return 0;
        }

        if (status === ExportStatus.POTENTIAL_CONFLICT) {
            return 1;
        }

        return 2;
    }

    private initXlsx(worksheetTitle: string, columns: DefinitionColumn[]): Excel.Workbook {
        const workbook = new Excel.Workbook();

        const sheet: Excel.Worksheet = workbook.addWorksheet(this.translocoService.translate(worksheetTitle));

        const baseColumns: DefinitionColumn[] = columns.map((column: DefinitionColumn): DefinitionColumn => ({
            header: column.header,
            key: column.key,
            width: DEFAULT_COLUMN_WIDTH,
        }));

        sheet.columns = baseColumns;

        return workbook;
    }

    private isEntityDeleted(entityId: string, editConflicts: EditConflicts): boolean {
        return editConflicts.deletedEntities.some((deletedEntityInfo) => deletedEntityInfo.entityId === entityId);
    }

    private isLayerDepositDeleted(layerId: string, editConflicts: EditConflicts): boolean {
        return editConflicts.layerIdsWithoutDeposit.includes(layerId);
    }

    private mapToRowData(
        projectId: string,
        entityId: string,
        entityEdit: EntityEdit,
        layerId: string,
        options: { editConflicts?: EditConflicts; layerName?: string },
    ): EditRowData {
        let before: Record<string, unknown> = {};

        if (entityEdit.action !== EditAction.CREATE) {
            before = this.editModeStateService.buildBefore(projectId, entityId);
        }

        const after = entityEdit.action === EditAction.DELETE || !entityEdit.properties ? {} : entityEdit.properties;
        const status = this.getExportStatus(layerId, entityId, options?.editConflicts);

        return {
            action: entityEdit.action,
            after,
            before,
            businessIdentifier: entityEdit.businessIdentifier.value,
            layerName: options?.layerName,
            propertyKeys: Object.keys(entityEdit.properties ?? {}),
            state: this.getStateLabel(status),
            status,
        };
    }

    private sortRows(a: EditRowData, b: EditRowData): number {
        return this.getStatusRank(a.status) - this.getStatusRank(b.status);
    }
}
